import {
  recordTemplateDecisionSchema,
  type PostTypeDto,
  type RecordTemplateDecisionDto,
  type TemplateDecisionClass,
  type TemplateDecisionRowDto,
  type VehicleTypeDto,
} from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7C §4] WHAT THE LIBRARY'S PREVIEW LEADS WITH — pure, so a test reads every branch without a render
 *  and `react-refresh/only-export-components` keeps the dialog's file to its component.
 *
 *    LIKELY — an undecided column whose server `suggestion` is a post or vehicle type (its likely
 *             master, `likelyColumnFor`): "Looks like X — use it?", one click to save that suggestion.
 *    CREATE — an undecided column with no such suggestion, for somebody who may create a post type: the
 *             post type master's own drawer, prefilled, as the decisions page offers it.
 *    FORM   — everything else: the decisions page's own form, unchanged.
 *
 *  ⚠️ THE SUGGESTION IS SENT AS IT STANDS, PARSED FIRST BY THE SHARED SCHEMA — the decisions page's
 *  "Use the suggestion" rule, so a body the server would refuse is never one click.
 *
 *  ⚠️ AND ONLY A MASTER THE SCREEN HAS LOADED IS NAMED. A suggestion naming a post type that is not in
 *  the active list (archived since, or not yet read) cannot be worded, so it is not led with — the
 *  screen falls to CREATE or the form rather than "Looks like no longer available — use it?".
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/** The decision a panel row opens: its class, its code, and the words the panel shows it under. */
export type DecisionModalTarget = {
  readonly class: TemplateDecisionClass;
  readonly code: string;
  readonly words: string;
};

export type ModalLead =
  | {
      readonly kind: 'LIKELY';
      readonly body: RecordTemplateDecisionDto;
      readonly name: string;
      readonly shortName: string | null;
      readonly vehicle: boolean;
    }
  | { readonly kind: 'CREATE' }
  | { readonly kind: 'FORM' };

export const modalLeadOf = (
  row: TemplateDecisionRowDto,
  mayCreatePostType: boolean,
  masters: { readonly postTypes: readonly PostTypeDto[]; readonly vehicleTypes: readonly VehicleTypeDto[] },
): ModalLead => {
  if (row.class !== 'FORCE_COLUMN' || row.decision !== null) return { kind: 'FORM' };
  const suggestion = row.suggestion;
  if (suggestion?.choice === 'POST_TYPE' || suggestion?.choice === 'VEHICLE_TYPE') {
    const body = recordTemplateDecisionSchema.safeParse({ class: row.class, code: row.code, decision: suggestion });
    const post =
      suggestion.choice === 'POST_TYPE'
        ? masters.postTypes.find((each) => each.id === suggestion.postTypeId)
        : undefined;
    const vehicle =
      suggestion.choice === 'VEHICLE_TYPE'
        ? masters.vehicleTypes.find((each) => each.id === suggestion.vehicleTypeId)
        : undefined;
    if (body.success && post)
      return { kind: 'LIKELY', body: body.data, name: post.name, shortName: post.shortName, vehicle: false };
    if (body.success && vehicle)
      return { kind: 'LIKELY', body: body.data, name: vehicle.name, shortName: null, vehicle: true };
  }
  return mayCreatePostType ? { kind: 'CREATE' } : { kind: 'FORM' };
};
